/**
 * The three authentication endpoints, as the frontend sees them (PHASE-0.md §2.1).
 *
 * Mirrors `api/src/Continuum.Api/Auth/AuthEndpoints.cs`.
 *
 * **There is no token to keep anywhere.** The session is an httpOnly cookie the browser
 * cannot read, which is why {@link fetchSession} exists at all: after a reload the frontend
 * holds a cookie whose contents are invisible to it, and the only way to find out who is
 * signed in is to ask. That is one request on page load, and it is deliberately not audited
 * as a read of the record — it reads no patient.
 *
 * **Nothing here caches.** A session that has expired server-side must present as signed
 * out on the next request rather than as a stale name in the app bar, and the cheapest way
 * to guarantee that is to have no second copy of the answer.
 */

/**
 * Who is signed in. Mirrors `SessionResponse`, field for field.
 *
 * Narrow on purpose: no account id, no practitioner id, no session id. The practitioner id
 * is `audit.event.actor_id`, and a browser that knows it is a browser that could send it
 * back — a client-supplied actor id in a legally authoritative log is the one claim this
 * system must never accept. `AuthEndpointsTests` holds the shape.
 */
export interface Session {
  /** The normalised username, so a clinician can see which account they are in. */
  readonly username: string

  /** The clinician's name, for the app bar. */
  readonly displayName: string

  /**
   * The role code — `physician`, `nurse`.
   *
   * What the interface renders a label from, in whatever language is selected, because a
   * code is the only form that can be translated (DECISIONS.md #35). Never the basis of a
   * decision here: {@link Session.canDocument} is the server's answer to that question.
   */
  readonly roleCode: string

  /**
   * The documented Swedish title, as stored — `läkare`.
   *
   * Record content in the documenter's own words (AGENTS.md §7), so it is shown as stored
   * and never translated. It is not a substitute for the label `roleCode` produces.
   */
  readonly roleTitle: string

  /**
   * Whether to offer the note form.
   *
   * **A courtesy, never the check.** The refusal that counts is server-side on the write,
   * where it is also audited. This exists so a role that may not document is not shown a
   * form that refuses them *after* they have typed a note — the worst possible moment.
   */
  readonly canDocument: boolean

  /**
   * Whether to offer the death form (DECISIONS.md #53) — a physician's alone, because the record
   * names whoever records it as having established the death. A courtesy, like
   * {@link Session.canDocument}; the server's gate is the check.
   */
  readonly canEstablishDeath: boolean
}

/**
 * Every code a login can be refused with.
 *
 * Exactly one, and that is the point: an unknown username, a wrong password and a
 * deactivated account are indistinguishable in the response. Which it was is in the audit
 * log, where a review can see it and an attacker cannot (`LoginService`).
 *
 * `NOT_AUTHENTICATED` is not in this list — it is the answer to {@link fetchSession}, not
 * to a login attempt, and conflating them would mean a login form showing "your session
 * has expired" to somebody who has not had one yet.
 */
export const LOGIN_REFUSAL_CODES = ['INVALID_CREDENTIALS'] as const

export type LoginRefusalCode = (typeof LOGIN_REFUSAL_CODES)[number]

/**
 * The credentials were refused.
 *
 * Distinct from {@link AuthUnavailableError} for the reason every screen in this codebase
 * separates the two: a refusal is something the clinician can fix by typing again, and an
 * unavailable API is something to report to the on-call technician. Telling one to do the
 * other wastes minutes in an outage.
 */
export class LoginRefusedError extends Error {
  readonly code: LoginRefusalCode | 'UNKNOWN'

  constructor(code: LoginRefusalCode | 'UNKNOWN') {
    super(`Login refused: ${code}`)
    this.name = 'LoginRefusedError'
    this.code = code
  }
}

/** The API could not be reached, or answered something unusable. */
export class AuthUnavailableError extends Error {
  constructor() {
    super('Authentication unavailable')
    this.name = 'AuthUnavailableError'
  }
}

function isRefusalCode(value: unknown): value is LoginRefusalCode {
  return typeof value === 'string' && (LOGIN_REFUSAL_CODES as readonly string[]).includes(value)
}

/**
 * Asks who is signed in.
 *
 * `null` means nobody, and it is an ordinary answer rather than a failure — it is what the
 * first request of a fresh page load gets. A 401 here carries a body with
 * `NOT_AUTHENTICATED` in it, which is how this is told apart from a 401 invented by a
 * proxy; either way the answer to the caller is the same, so the code is not returned.
 *
 * Throws {@link AuthUnavailableError} only when the box did not answer at all. The
 * difference matters: "not signed in" shows a login form, and "unavailable" must not,
 * because a clinician typing a password into a form that cannot submit it is a clinician
 * who thinks the system is refusing them.
 */
export async function fetchSession(signal: AbortSignal): Promise<Session | null> {
  let response: Response

  try {
    response = await fetch('/api/auth/session', {
      signal,
      headers: { Accept: 'application/json' },
    })
  } catch (cause) {
    if (signal.aborted) {
      throw cause
    }
    throw new AuthUnavailableError()
  }

  if (response.status === 401) {
    return null
  }

  if (!response.ok) {
    throw new AuthUnavailableError()
  }

  try {
    return (await response.json()) as Session
  } catch {
    throw new AuthUnavailableError()
  }
}

/**
 * Signs in, and returns the session on success.
 *
 * Sends exactly what was typed. **The password is not trimmed** — `PasswordHash` does not
 * trim either, and a client that silently dropped a leading space would make a password
 * that works through one client fail through another.
 *
 * Every attempt writes an audit row on the server, successful or not, before this returns.
 */
export async function login(
  username: string,
  password: string,
  signal: AbortSignal,
): Promise<Session> {
  let response: Response

  try {
    response = await fetch('/api/auth/login', {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ username, password }),
    })
  } catch (cause) {
    if (signal.aborted) {
      throw cause
    }
    throw new AuthUnavailableError()
  }

  if (response.status === 401) {
    let code: unknown

    try {
      const body = (await response.json()) as { error?: { code?: unknown } }
      code = body.error?.code
    } catch {
      code = undefined
    }

    throw new LoginRefusedError(isRefusalCode(code) ? code : 'UNKNOWN')
  }

  if (!response.ok) {
    throw new AuthUnavailableError()
  }

  try {
    return (await response.json()) as Session
  } catch {
    // A 200 whose body did not parse means the cookie was probably issued and this client
    // cannot say who for. Reported as unavailable rather than as a refusal, so the screen
    // does not tell a clinician their password was wrong when it was not.
    throw new AuthUnavailableError()
  }
}

/**
 * Discards the session.
 *
 * Resolves rather than throwing on anything short of an unreachable box: 204 and 401 are
 * both "you are signed out now", and logging out of a session that has already expired has
 * to work or a clinician is looking at a screen they cannot leave.
 *
 * **Not audited**, which is a recorded gap and not a judgement that it does not matter —
 * there is no `logout` action in the audit schema, and adding one is a schema change
 * (DECISIONS.md #39).
 */
export async function logout(signal: AbortSignal): Promise<void> {
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      signal,
      headers: { Accept: 'application/json' },
    })
  } catch (cause) {
    if (signal.aborted) {
      throw cause
    }
    throw new AuthUnavailableError()
  }
}
